import {
  reactErrorHandler,
  captureException as sentryCaptureException,
  init as sentryInit,
  isInitialized as sentryIsInitialized,
} from "@sentry/react";
import type { RootOptions } from "react-dom/client";
import { logger } from "@/lib/logging";
import { redactSensitiveKeys } from "@/lib/observability/redaction";
import { scheduleDeferredIntegrations } from "./deferred-loader";
import { getSentryEnv } from "./env";
import { isExtensionNoise } from "./extension-noise";
import { isKnownReplicacheStorageLoss } from "./replicache-storage-loss";

const INSFORGE_HOST_RE = /^https:\/\/(?:[A-Za-z0-9-]+\.)*insforge\.app(?::\d+)?(?:[/?#]|$)/;
const LOCALHOST_RE = /^https?:\/\/localhost(?::\d+)?(?:[/?#]|$)/;

/** Check if Sentry DSN is configured. */
export function isConfigured(): boolean {
  return getSentryEnv() !== null;
}

/** Check if Sentry has been initialized. */
export function isInitialized(): boolean {
  return sentryIsInitialized();
}

/** Initialize Sentry with privacy-safe defaults. */
export function init(): void {
  if (isInitialized()) {
    return;
  }

  const env = getSentryEnv();
  if (!env) {
    logger.info("[sentry] not configured — set VITE_SENTRY_DSN to enable");
    return;
  }

  const isProduction = env.environment === "production";

  sentryInit({
    dsn: env.dsn,
    environment: env.environment,
    release: env.release,

    // Privacy: disable user info and body collection
    dataCollection: {
      userInfo: false,
      httpBodies: [],
    },
    sendDefaultPii: false,
    // Filter known noise before redaction obscures the exception fingerprint.
    // All other errors retain the existing privacy and reporting behavior.
    beforeSend: (event) =>
      isExtensionNoise(event) || isKnownReplicacheStorageLoss(event)
        ? null
        : (redactSensitiveKeys(event) as typeof event),

    tracesSampleRate: isProduction ? 0.1 : 1.0,
    tracePropagationTargets: [LOCALHOST_RE, INSFORGE_HOST_RE],

    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    enableLogs: !isProduction,
  });

  // Error capture and React root handlers stay active from bootstrap. Browser
  // tracing and Replay are substantially larger optional instrumentation, so
  // attach them after the initial page load instead of keeping them in the
  // eager application graph.
  scheduleDeferredIntegrations();
}

/** Provide React error handlers for root element. */
export function getReactRootOptions(): RootOptions | undefined {
  if (!(isConfigured() && isInitialized())) {
    return;
  }

  return {
    onCaughtError: reactErrorHandler(),
    onUncaughtError: reactErrorHandler(),
    onRecoverableError: reactErrorHandler(),
  };
}

/**
 * Structured context attached to an explicit capture. Callers own the privacy
 * contract: supply enums, booleans, and opaque ids only — events still pass
 * the `beforeSend` redactor, but that is a backstop, not a license.
 */
export interface CaptureExceptionContext {
  contexts?: Record<string, Record<string, unknown>>;
  level?: "error" | "warning";
  tags?: Record<string, string | number | boolean>;
}

/** Capture an exception through Sentry, with optional structured context. */
export function captureException(error: unknown, context?: CaptureExceptionContext): void {
  if (!(isConfigured() && isInitialized())) {
    return;
  }
  sentryCaptureException(error, context);
}
